import * as _ from 'lodash';
import * as moment from 'moment';
import router from '@/router';
import * as Mutations from './mutations';
import * as Routes from '@/router/routes';
import { SCHOOL_APP } from '@/main';
import { AftercareDay } from './aftercare-day';
import { AftercareAddOn } from './aftercare-add-on';
import { TYPES } from '@/constants/ages-grades';
import { canGoToStep, setTodayIfPastDate } from '@/common/utils/step-form';
import { SchoolAftercareService } from '@/common/services/pages/aftercare.service';
import { PaginationParams } from '@/common/utils/pagination-params';
import {
    DEFAULT_STATE,
    DEFAULT_STATE_MULTI_CHILD_DISCOUNT,
    DEFAULT_STATE_MULTI_DAY_DISCOUNT,
    STEPS
} from '@/store/modules/aftercare-form/default-state';


export const AftercareFormModule = {
    state: {
        isSubmitted: false,
        step: 1,
        form: _.cloneDeep(DEFAULT_STATE),
        isDraft: false,
        steps: STEPS.map(el => ({
            name: el,
            valid: false
        })),
        addOnsOpened: false,
        billingRateActive: false,
        billingTypeActive: false,
        chargeTypeActive: false,
        dropInBillingRateActive: false,
        aftercareAddOns: null,
        discountsOpened: false,
        detailsOpened: false,
        needsValidation: false,
        formCopy: _.cloneDeep(DEFAULT_STATE),
        duplicateAftercareForm: false
    },

    mutations: {
        [Mutations.AFTERCARE_FORM_NEXT_STEP](state) {
            state.step++;
        },

        [Mutations.AFTERCARE_FORM_GOTO_STEP](state, number) {
            if (canGoToStep(state, number)) {
                state.step = number;
            }
        },

        [Mutations.AFTERCARE_FORM_SET_FORM_VALID](state, valid) {
            if (valid === undefined) {
                state.steps.forEach(step => step.valid = true);
            }
            else {
                state.steps[state.step - 1].valid = valid;
            }
        },

        [Mutations.AFTERCARE_FORM_SUBMIT](state, isSubmitted = true) {
            state.form = _.cloneDeep(DEFAULT_STATE);
            state.isSubmitted = isSubmitted;
            state.steps.forEach(step => step.valid = false);
            state.discountsOpened = false;
            state.addOnsOpened = false;
            state.detailsOpened = false;
            state.formCopy = _.cloneDeep(state.form);
        },

        [Mutations.AFTERCARE_FORM_RESET](state) {
            state.form = _.cloneDeep(DEFAULT_STATE);
            state.isSubmitted = false;
            state.step = 1;
            state.steps.forEach(step => step.valid = false);
            state.discountsOpened = false;
            state.addOnsOpened = false;
            state.detailsOpened = false;
            state.formCopy = _.cloneDeep(state.form);
        },
        [Mutations.AFTERCARE_FORM_SET_IS_DRAFT](state, isDraft) { 
            state.isDraft = isDraft;
        },
        [Mutations.AFTERCARE_FORM_PREFILL](state, form) {
            state.steps.forEach(step => {
                step.valid = true;
            });
            if (!form.prorate) {
                form.prorate = state.form.prorate;
            }
            if (!form.multiChildDiscount) {
                form.multiChildDiscount = _.cloneDeep(DEFAULT_STATE_MULTI_CHILD_DISCOUNT);
            }
            if (!form.multiDayDiscount) {
                form.multiDayDiscount = _.cloneDeep(DEFAULT_STATE_MULTI_DAY_DISCOUNT);
            }
            if (form.multiChildDiscount && form.multiChildDiscount.active ||
                form.multiDayDiscount && form.multiDayDiscount.active) {
                state.discountsOpened = true;
            }
            if (form.pickUpOptions || form.teacher || form.tshirt || form.formIds.length) {
                state.detailsOpened = true;
            }
            if (!isEditForm()) {
                setTodayIfPastDate(form, 'signUpBeginsDate');
                setTodayIfPastDate(form, 'aftercareStartDate');
            }
            if (form.addOns.length) {
                state.addOnsOpened = true;
            }
            delete form.id;
            state.form = form;
            state.formCopy = _.cloneDeep(form);
        },

        [Mutations.AFTERCARE_FORM_DUPLICATE_TOGGLE](state, duplicateAftercareForm) {
            state.duplicateAftercareForm = duplicateAftercareForm;
        },

// STEP 1
        [Mutations.AFTERCARE_FORM_SET_AFTERCARE_NAME](state, aftercareName) {
            state.form.aftercareName = aftercareName;
        },

        [Mutations.AFTERCARE_FORM_SET_LOCATION_ID](state, location) {
            state.form.locationId = location.id;
        },

        [Mutations.AFTERCARE_FORM_SET_AGE](state, age) {
            state.form.ageFrom = age[0];
            state.form.ageTo = age[1];
            state.form.gradeFrom = null;
            state.form.gradeTo = null;
        },

        [Mutations.AFTERCARE_FORM_SET_GRADE](state, grade) {
            state.form.gradeFrom = grade[0];
            state.form.gradeTo = grade[1];
            state.form.ageFrom = null;
            state.form.ageTo = null;
        },

        [Mutations.AFTERCARE_FORM_SET_AGE_FROM](state, ageFrom) {
            state.form.ageFrom = ageFrom;
            resetGrade(state);
        },

        [Mutations.AFTERCARE_FORM_SET_AGE_TO](state, ageTo) {
            state.form.ageTo = ageTo;
            resetGrade(state);
        },

        [Mutations.AFTERCARE_FORM_SET_GRADE_FROM](state, gradeFrom) {
            state.form.gradeFrom = gradeFrom;
            resetAge(state);
        },

        [Mutations.AFTERCARE_FORM_SET_GRADE_TO](state, gradeTo) {
            state.form.gradeTo = gradeTo;
            resetAge(state);
        },

        [Mutations.AFTERCARE_FORM_SET_AFTERCARE_DESCRIPTION](state, aftercareDescription) {
            state.form.description = aftercareDescription;
        },

// STEP 2
        [Mutations.AFTERCARE_FORM_SET_AFTERCARE_START_DATE](state, date) {
            state.form.aftercareStartDate = date;
        },

        [Mutations.AFTERCARE_FORM_SET_AFTERCARE_END_DATE](state, date) {
            state.form.aftercareEndDate = date;
        },

        [Mutations.AFTERCARE_FORM_SET_NO_AFTERCARE_DATES](state, dates) {
            state.form.noAftercareDates = dates;
        },

        [Mutations.AFTERCARE_FORM_SET_SIGN_UP_BEGINS_DATE](state, signUpBegins) {
            state.form.signUpBeginsDate = signUpBegins;
        },

        [Mutations.AFTERCARE_FORM_SET_SIGN_UP_BEGINS_TIME](state, signUpBegins) {
            state.form.signUpBeginsTime = signUpBegins;
        },

        [Mutations.AFTERCARE_FORM_SET_SIGN_UP_ENDS_DATE](state, signUpEnds) {
            state.form.signUpEndsDate = signUpEnds;
        },

        [Mutations.AFTERCARE_FORM_SET_SIGN_UP_ENDS_TIME](state, signUpEnds) {
            state.form.signUpEndsTime = signUpEnds;
        },

//STEP 3
        [Mutations.AFTERCARE_FORM_ADD_DAY](state, day) {
            const sorterByDay = {
                "sunday": 0,
                "monday": 1,
                "tuesday": 2,
                "wednesday": 3,
                "thursday": 4,
                "friday": 5,
                "saturday": 6
            };

            state.form.days.push(new AftercareDay({ day }));

            state.form.days.sort((first, second) => {
                return sorterByDay[first.day.toLowerCase()] - sorterByDay[second.day.toLowerCase()];
            });
        },

        [Mutations.AFTERCARE_FORM_SET_DAY_INSTRUCTOR_ID](state, { instructor, id }) {
            if (id) {
                state.form.days.find(el => el.id === id).instructorId = instructor.id;
            }
        },

        [Mutations.AFTERCARE_FORM_SET_DAY_TIME_FROM](state, { timeFrom, id }) {
            if (id) {
                state.form.days.find(el => el.id === id).timeFrom = timeFrom;
            }
        },

        [Mutations.AFTERCARE_FORM_SET_DAY_TIME_TO](state, { timeTo, id }) {
            if (id) {
                state.form.days.find(el => el.id === id).timeTo = timeTo;
            }
        },

        [Mutations.AFTERCARE_FORM_SET_DAY_MAX_ENROLL](state, { maxEnroll, id }) {
            if (id) {
                state.form.days.find(el => el.id === id).maxEnroll = maxEnroll;
            }
        },

        [Mutations.AFTERCARE_FORM_SET_DAY_WAITLIST](state, { waitList, id }) {
            if (id) {
                state.form.days.find(el => el.id === id).waitList = waitList;
            }
        },

        [Mutations.AFTERCARE_FORM_SET_DAY_MAX_WAITLIST](state, { maxWaitList, id }) {
            if (id) {
                state.form.days.find(el => el.id === id).maxWaitList = maxWaitList;
            }
        },

        [Mutations.AFTERCARE_FORM_SET_DAY_DROPIN](state, { dropIn, id }) {
            if (id) {
                state.form.days.find(el => el.id === id).dropIn = dropIn;
            }
        },

        [Mutations.AFTERCARE_FORM_SET_DAY_MAX_DROPIN](state, { maxDropIn, id }) {
            if (id) {
                state.form.days.find(el => el.id === id).maxDropIn = maxDropIn;
            }
        },

        [Mutations.AFTERCARE_FORM_REMOVE_DAY](state, day) {
            const stateDay = state.form.days.find(el => el.day === day);
            if (stateDay) {
                if (stateDay.new || !stateDay.id) {
                    const dayOnIndex = state.form.days.findIndex(el => el.day === day);
                    state.form.days.splice(dayOnIndex, 1);
                } else {
                    stateDay.active = false;
                }
            }
        },

//STEP 4
// Block Session Fees
        [Mutations.AFTERCARE_FORM_SET_PRICE](state, price) {
            state.form.price = price;
        },

        [Mutations.AFTERCARE_FORM_SET_BILLING_TYPE](state, billingType) {
            state.form.billingType = billingType;
        },

        [Mutations.AFTERCARE_FORM_SET_CHARGE_TYPE](state, chargeType) {
            state.form.chargeType = chargeType;
        },

        [Mutations.AFTERCARE_FORM_SET_BILLING_RATE](state, billingRate) {
            state.form.billingRate = billingRate;
        },

// Block Drop-In
        [Mutations.AFTERCARE_FORM_SET_DROP_IN_PRICE](state, price) {
            state.form.dropInPrice = price;
        },

        [Mutations.AFTERCARE_FORM_SET_DROP_IN_BILLING_RATE](state, billingRate) {
            state.form.dropInBillingRate = billingRate;
        },

// Block Prorate
        [Mutations.AFTERCARE_FORM_SET_PRORATE](state, prorate) {
            state.form.prorate = prorate;
        },

// Block Discounts
        [Mutations.AFTERCARE_FORM_SET_DISCOUNTS_OPENED](state, discountsOpened) {
            state.discountsOpened = discountsOpened;
        },

        [Mutations.AFTERCARE_FORM_SET_DISCOUNTS_DAY_ACTIVE](state, active) {
            state.form.multiDayDiscount.active = active;
        },

        [Mutations.AFTERCARE_FORM_SET_DISCOUNTS_TWO_DAYS_AMOUNT](state, twoDaysAmount) {
            state.form.multiDayDiscount.twoDaysAmount = twoDaysAmount;
        },

        [Mutations.AFTERCARE_FORM_SET_DISCOUNTS_THREE_DAYS_AMOUNT](state, threeDaysAmount) {
            state.form.multiDayDiscount.threeDaysAmount = threeDaysAmount;
        },

        [Mutations.AFTERCARE_FORM_SET_DISCOUNTS_FOUR_DAYS_AMOUNT](state, fourDaysAmount) {
            state.form.multiDayDiscount.fourDaysAmount = fourDaysAmount;
        },

        [Mutations.AFTERCARE_FORM_SET_DISCOUNTS_FIVE_DAYS_AMOUNT](state, fiveDaysAmount) {
            state.form.multiDayDiscount.fiveDaysAmount = fiveDaysAmount;
        },

        [Mutations.AFTERCARE_FORM_SET_DISCOUNTS_SIX_DAYS_AMOUNT](state, sixDaysAmount) {
            state.form.multiDayDiscount.sixDaysAmount = sixDaysAmount;
        },

        [Mutations.AFTERCARE_FORM_SET_DISCOUNTS_SEVEN_DAYS_AMOUNT](state, sevenDaysAmount) {
            state.form.multiDayDiscount.sevenDaysAmount = sevenDaysAmount;
        },

        [Mutations.AFTERCARE_FORM_SET_DISCOUNTS_CHILD_ACTIVE](state, active) {
            state.form.multiChildDiscount.active = active;
        },

        [Mutations.AFTERCARE_FORM_SET_DISCOUNTS_TWO_CHILDREN_AMOUNT](state, twoChildrenAmount) {
            state.form.multiChildDiscount.twoChildrenAmount = twoChildrenAmount;
        },

        [Mutations.AFTERCARE_FORM_SET_DISCOUNTS_THREE_CHILDREN_AMOUNT](state, threeChildrenAmount) {
            state.form.multiChildDiscount.threeChildrenAmount = threeChildrenAmount;
        },

        [Mutations.AFTERCARE_FORM_SET_DISCOUNTS_FOUR_CHILDREN_AMOUNT](state, fourChildrenAmount) {
            state.form.multiChildDiscount.fourChildrenAmount = fourChildrenAmount;
        },

        [Mutations.AFTERCARE_FORM_SET_DISCOUNTS_FIVE_CHILDREN_AMOUNT](state, fiveChildrenAmount) {
            state.form.multiChildDiscount.fiveChildrenAmount = fiveChildrenAmount;
        },

        [Mutations.AFTERCARE_FORM_SET_DISCOUNTS_SIX_CHILDREN_AMOUNT](state, sixChildrenAmount) {
            state.form.multiChildDiscount.sixChildrenAmount = sixChildrenAmount;
        },

        [Mutations.AFTERCARE_FORM_SET_DISCOUNTS_SEVEN_CHILDREN_AMOUNT](state, sevenChildrenAmount) {
            state.form.multiChildDiscount.sevenChildrenAmount = sevenChildrenAmount;
        },

// Block AddOns
        [Mutations.AFTERCARE_FORM_ADD_ADDON](state) {
            state.form.addOns.push(new AftercareAddOn());
        },

        [Mutations.AFTERCARE_FORM_SET_ADDON_NAME](state, { name, id }) {
            if (id) {
                state.form.addOns.find(addOn => addOn.id === id).name = name;
            }
        },

        [Mutations.AFTERCARE_FORM_SET_ADDON_PRICE](state, { price, id }) {
            if (id) {
                state.form.addOns.find(addOn => addOn.id === id).price = price;
            }
        },

        [Mutations.AFTERCARE_FORM_SET_ADDON_DISCOUNT](state, { discount, id }) {
            if (id) {
                state.form.addOns.find(addOn => addOn.id === id).discount = discount;
            }
        },

        [Mutations.AFTERCARE_FORM_SET_ADDON_REQUIRED](state, { required, id }) {
            if (id) {
                state.form.addOns.find(addOn => addOn.id === id).required = required;
            }
        },

        [Mutations.AFTERCARE_FORM_SET_ADDON_MAX_AMOUNT](state, { maxAmount, id }) {
            if (id) {
                state.form.addOns.find(addOn => addOn.id === id).maxAmount = maxAmount;
            }
        },

        [Mutations.AFTERCARE_FORM_REMOVE_AFTERCARE_ADDON](state, id) {
            const addOn = state.form.addOns.find(addOn => addOn.id === id);
            if (addOn) {
                if (addOn.new) {
                    const addOnIndex = state.form.addOns.findIndex(addOn => addOn.id === id);
                    state.form.addOns.splice(addOnIndex, 1);
                }
                else {
                    addOn.active = false;
                }
            }
        },

        [Mutations.AFTERCARE_FORM_SET_ADDONS_OPENED](state, addOnsOpened) {
            state.addOnsOpened = addOnsOpened;
        },

// Block Other Details
        [Mutations.AFTERCARE_FORM_SET_DETAILS_OPENED](state, detailsOpened) {
            state.detailsOpened = detailsOpened;
        },

        [Mutations.AFTERCARE_FORM_SET_PICKUP_OPTIONS](state, pickUpOptions) {
            state.form.pickUpOptions = pickUpOptions;
        },

        [Mutations.AFTERCARE_FORM_SET_PICKUP_OPTIONS_REQUIRED](state, pickUpOptionsRequired) {
            state.form.pickUpOptionsRequired = pickUpOptionsRequired;
        },

        [Mutations.AFTERCARE_FORM_SET_TEACHER](state, teacher) {
            state.form.teacher = teacher;
        },

        [Mutations.AFTERCARE_FORM_SET_TEACHER_REQUIRED](state, teacherRequired) {
            state.form.teacherRequired = teacherRequired;
        },

        [Mutations.AFTERCARE_FORM_SET_T_SHIRT](state, tshirt) {
            state.form.tshirt = tshirt;
        },

        [Mutations.AFTERCARE_FORM_SET_T_SHIRT_REQUIRED](state, tshirtRequired) {
            state.form.tshirtRequired = tshirtRequired;
        },

        [Mutations.SET_AFTERCARE_ADDONS](state, aftercareAddOns) {
            state.aftercareAddOns = aftercareAddOns;
        }
    },

    actions: {
        async aftercareCreationNextStep(context) {
            if (context.getters.aftercareCreationStep === STEPS.length) {
                SCHOOL_APP.$emit('validate-aftercare-form');
            }
            else {
                context.commit(Mutations.AFTERCARE_FORM_NEXT_STEP);
            }
        },
        async saveDraftAftercare(context, activateDraftForm = false) {
            if (context.getters.aftercareCreationStep === STEPS.length) {
                SCHOOL_APP.$emit('validate-aftercare-form-draft', activateDraftForm);
            }
        },
        async submitAftercareForm(context, showProrate) {
            if (!context.getters.showProgress) {
                const aftercareId = getAftercareId();
                const form = context.getters.aftercareCreationForm;
                form.addOns.forEach(addOn => {
                    if (addOn.new) {
                        delete addOn.id;
                    }
                    delete addOn.new;
                });
                form.days.forEach(day => {
                    !day.waitList && (day.maxWaitList = 0);
                    !day.dropIn && (day.maxDropIn = 0);

                    if (day.new) {
                        delete day.id;
                    }
                    delete day.new;
                });
                if (!context.getters.aftercareFormAddOnsOpened) {
                    form.addOns = [];
                }
                if (form.chargeType === 'ATTENDANCE') {
                    form.prorate = false;
                }
                if (!showProrate) {
                    form.prorate = false;
                }
                if (form.price === 0) {
                    form.billingRate = null;
                    form.billingType = null;
                    form.chargeType = null;
                    form.prorate = false;
                    form.multiChildDiscount.active = false;
                    form.multiDayDiscount.active = false;
                    form.multiDayDiscount.twoDaysAmount = null;
                    form.multiDayDiscount.threeDaysAmount = null;
                    form.multiDayDiscount.fourDaysAmount = null;
                    form.multiDayDiscount.fiveDaysAmount = null;
                    form.multiDayDiscount.sixDaysAmount = null;
                    form.multiDayDiscount.sevenDaysAmount = null;
                    form.multiChildDiscount.twoChildrenAmount = null;
                    form.multiChildDiscount.threeChildrenAmount = null;
                    form.multiChildDiscount.fourChildrenAmount = null;
                    form.multiChildDiscount.fiveChildrenAmount = null;
                    form.multiChildDiscount.sixChildrenAmount = null;
                    form.multiChildDiscount.sevenChildrenAmount = null;
                }
                if (form.dropInPrice === 0) {
                    form.dropInBillingRate = null;
                }
                if (!context.getters.aftercareFormDetailsOpened) {
                    form.pickUpOptions = false;
                    form.pickUpOptionsRequired = false;
                    form.teacher = false;
                    form.teacherRequired = false;
                    form.tshirt = false;
                    form.tshirtRequired = false;
                }
                if (!context.getters.aftercareFormDiscountsOpened) {
                    form.multiChildDiscount.active = false;
                    form.multiDayDiscount.active = false;
                }
                if (!form.multiDayDiscount.active) {
                    form.multiDayDiscount.twoDaysAmount = null;
                    form.multiDayDiscount.threeDaysAmount = null;
                    form.multiDayDiscount.fourDaysAmount = null;
                    form.multiDayDiscount.fiveDaysAmount = null;
                    form.multiDayDiscount.sixDaysAmount = null;
                    form.multiDayDiscount.sevenDaysAmount = null;
                }
                if (!form.multiChildDiscount.active) {
                    form.multiChildDiscount.twoChildrenAmount = null;
                    form.multiChildDiscount.threeChildrenAmount = null;
                    form.multiChildDiscount.fourChildrenAmount = null;
                    form.multiChildDiscount.fiveChildrenAmount = null;
                    form.multiChildDiscount.sixChildrenAmount = null;
                    form.multiChildDiscount.sevenChildrenAmount = null;
                }

                if (aftercareId) {
                    await SchoolAftercareService.editAftercareSession(context.getters.schoolId, aftercareId, form);
                    context.commit(Mutations.AFTERCARE_FORM_SUBMIT);
                    router.push({ name: Routes.AFTERCARE_PAGE });
                    context.dispatch('createSuccessNotification', 'You have successfully edited a session');
                }
                else {
                    form.multiDayDiscount && delete form.multiDayDiscount.id;
                    form.multiChildDiscount && delete form.multiChildDiscount.id;
                    form.addOns.forEach(addOn => delete addOn.id);
                    form.addOns = form.addOns.filter(addOn => addOn.active);
                    form.days.forEach(day => delete day.id);
                    form.days = form.days.filter(day => day.active);
                    await SchoolAftercareService.createAftercareSession(context.getters.schoolId, form);
                    context.commit(Mutations.AFTERCARE_FORM_SUBMIT);
                    context.getters.duplicateAftercareForm && context.commit(Mutations.AFTERCARE_FORM_DUPLICATE_TOGGLE, false);
                }
            }
        },
        async submitAftercareFormDraft(context, { showProrate, activateDraftForm }) {
            if (!context.getters.showProgress) {
                const aftercareId = getAftercareId();
                const form = context.getters.aftercareCreationForm;
                form.addOns.forEach(addOn => {
                    if (addOn.new) {
                        delete addOn.id;
                    }
                    delete addOn.new;
                });
                form.days.forEach(day => {
                    !day.waitList && (day.maxWaitList = 0);
                    !day.dropIn && (day.maxDropIn = 0);

                    if (day.new) {
                        delete day.id;
                    }
                    delete day.new;
                });
                if (!context.getters.aftercareFormAddOnsOpened) {
                    form.addOns = [];
                }
                if (form.chargeType === 'ATTENDANCE') {
                    form.prorate = false;
                }
                if (!showProrate) {
                    form.prorate = false;
                }
                if (form.price === 0) {
                    form.billingRate = null;
                    form.billingType = null;
                    form.chargeType = null;
                    form.prorate = false;
                    form.multiChildDiscount.active = false;
                    form.multiDayDiscount.active = false;
                    form.multiDayDiscount.twoDaysAmount = null;
                    form.multiDayDiscount.threeDaysAmount = null;
                    form.multiDayDiscount.fourDaysAmount = null;
                    form.multiDayDiscount.fiveDaysAmount = null;
                    form.multiDayDiscount.sixDaysAmount = null;
                    form.multiDayDiscount.sevenDaysAmount = null;
                    form.multiChildDiscount.twoChildrenAmount = null;
                    form.multiChildDiscount.threeChildrenAmount = null;
                    form.multiChildDiscount.fourChildrenAmount = null;
                    form.multiChildDiscount.fiveChildrenAmount = null;
                    form.multiChildDiscount.sixChildrenAmount = null;
                    form.multiChildDiscount.sevenChildrenAmount = null;
                }
                if (form.dropInPrice === 0) {
                    form.dropInBillingRate = null;
                }
                if (!context.getters.aftercareFormDetailsOpened) {
                    form.pickUpOptions = false;
                    form.pickUpOptionsRequired = false;
                    form.teacher = false;
                    form.teacherRequired = false;
                    form.tshirt = false;
                    form.tshirtRequired = false;
                }
                if (!context.getters.aftercareFormDiscountsOpened) {
                    form.multiChildDiscount.active = false;
                    form.multiDayDiscount.active = false;
                }
                if (!form.multiDayDiscount.active) {
                    form.multiDayDiscount.twoDaysAmount = null;
                    form.multiDayDiscount.threeDaysAmount = null;
                    form.multiDayDiscount.fourDaysAmount = null;
                    form.multiDayDiscount.fiveDaysAmount = null;
                    form.multiDayDiscount.sixDaysAmount = null;
                    form.multiDayDiscount.sevenDaysAmount = null;
                }
                if (!form.multiChildDiscount.active) {
                    form.multiChildDiscount.twoChildrenAmount = null;
                    form.multiChildDiscount.threeChildrenAmount = null;
                    form.multiChildDiscount.fourChildrenAmount = null;
                    form.multiChildDiscount.fiveChildrenAmount = null;
                    form.multiChildDiscount.sixChildrenAmount = null;
                    form.multiChildDiscount.sevenChildrenAmount = null;
                }

                if (aftercareId) {
                    if (activateDraftForm) {
                        await SchoolAftercareService.editAftercareSession(context.getters.schoolId, aftercareId, {...form, draft: false});
                        context.commit(Mutations.AFTERCARE_FORM_SUBMIT);
                        router.push({ name: Routes.AFTERCARE_PAGE });
                        context.dispatch('createSuccessNotification', 'You have successfully activated a session');
                    } else {
                        await SchoolAftercareService.editAftercareSession(context.getters.schoolId, aftercareId, {...form, draft: true});
                        context.commit(Mutations.AFTERCARE_FORM_SUBMIT);
                        router.push({ name: Routes.AFTERCARE_PAGE });
                        context.dispatch('createSuccessNotification', 'You have successfully edited a session');
                    }
                }
                else {
                    form.multiDayDiscount && delete form.multiDayDiscount.id;
                    form.multiChildDiscount && delete form.multiChildDiscount.id;
                    form.addOns.forEach(addOn => delete addOn.id);
                    form.addOns = form.addOns.filter(addOn => addOn.active);
                    form.days.forEach(day => delete day.id);
                    form.days = form.days.filter(day => day.active);
                    await SchoolAftercareService.createAftercareSession(context.getters.schoolId, {...form, draft: true});
                    context.commit(Mutations.AFTERCARE_FORM_SUBMIT);
                    context.getters.duplicateAftercareForm && context.commit(Mutations.AFTERCARE_FORM_DUPLICATE_TOGGLE, false);
                }
            }
        },
        async prefillAftercareForm(context, aftercareId) {
            try {
                const aftercareFormResponse = await SchoolAftercareService.getAftercareSession(context.getters.schoolId, aftercareId);
                
                const aftercareDraftListResponse = await SchoolAftercareService.getAftercareList(context.getters.schoolId, PaginationParams.createParams({filters: {statuses: "DRAFT"}}));
                
                const draftIds = aftercareDraftListResponse.data.content.map(i => i.id);
                
                const isDraft = draftIds.includes(aftercareFormResponse.data.id);
                
                context.commit(Mutations.AFTERCARE_FORM_SET_IS_DRAFT, isDraft);
                if (isDraft) {
                    context.commit(Mutations.AFTERCARE_FORM_PREFILL, validateAftercareDatesAndTimes(aftercareFormResponse.data));
                } else {
                    context.commit(Mutations.AFTERCARE_FORM_PREFILL, aftercareFormResponse.data);
                }
            }
            catch (e) {
                throw e
            }
        },

        goToAftercareCreationStep(context, number) {
            context.commit(Mutations.AFTERCARE_FORM_GOTO_STEP, number);
        },

        goToAftercareCreationStart(context) {
            context.dispatch('goToAftercareCreationStep', 1);
            context.commit(Mutations.AFTERCARE_FORM_SUBMIT, false);
        },

        resetAftercareForm(context) {
            context.commit(Mutations.AFTERCARE_FORM_RESET);
        },

        async getAftercareAddOns(context, aftercareId) {
            const { data } = await SchoolAftercareService.getAftercareAddOns(context.getters.schoolId, aftercareId);
            context.commit(Mutations.SET_AFTERCARE_ADDONS, data);
        }
    },

    getters: {
        aftercareCreationForm: state => state.form,

        aftercareCreationStep: state => state.step,

        aftercareCreationSteps: state => state.steps,

        aftercareCreationFormDisabled: (state, getters, rootState, rootGetters) => state.steps &&
        state.step === state.steps.length
            ? state.steps.some(step => !step.valid)
            : !state.steps[state.step - 1].valid || rootGetters.showProgress,

        aftercareCreationType: state => state.form.gradeFrom ? TYPES[0] : TYPES[1],

        aftercareCreationFormSubmitted: state => state.isSubmitted,

        aftercareFormDiscountsOpened: state => state.discountsOpened,

        aftercareFormAddOnsOpened: state => state.addOnsOpened,

        aftercareFormDetailsOpened: state => state.detailsOpened,

        aftercareFormDirty: state => !_.isEqual(state.form, state.formCopy),

        aftercareAddOns: state => state.aftercareAddOns,

        duplicateAftercareForm: state => state.duplicateAftercareForm,

        isAftercareFormDraft: (state) => state.isDraft,
        
        isAftercareCostEditingDisabled: (state) => {
            if (state.form && isEditForm()) {
                return !state.isDraft;
            }
            return false;
        }
    }
};

function resetAge(state) {
    state.form.ageFrom = null;
    state.form.ageTo = null;
}

function resetGrade(state) {
    state.form.gradeFrom = null;
    state.form.gradeTo = null;
}

function getAftercareId() {
    return router.app._route.params.id;
}

function isEditForm() {
    return !!getAftercareId();
}

function validateAftercareDatesAndTimes(aftercareData) {
    const now = moment();
  
    // Convert array dates to moment objects and validate
    if (Array.isArray(aftercareData.aftercareStartDate)) {
      let startDate = moment(aftercareData.aftercareStartDate);
      if (startDate.isBefore(now, "day")) {
        aftercareData.aftercareStartDate = [now.year(), now.month() + 1, now.date()];
      }
    }
  
    if (Array.isArray(aftercareData.aftercareEndDate)) {
      let endDate = moment(aftercareData.aftercareEndDate);
      if (endDate.isBefore(now, "day")) {
        aftercareData.aftercareEndDate = [now.year(), now.month() + 1, now.date()];
      }
    }
  
    // Validate sign-up dates
    if (moment(aftercareData.signUpBeginsDate).isBefore(now, "day")) {
      aftercareData.signUpBeginsDate = now.format("YYYY-MM-DD");
    }
  
    if (moment(aftercareData.signUpEndsDate).isBefore(now, "day")) {
      aftercareData.signUpEndsDate = now.format("YYYY-MM-DD");
    }
  
    // Validate sign-up date + time combinations
    const signUpBeginsDateTime = moment(`${aftercareData.signUpBeginsDate} ${aftercareData.signUpBeginsTime}`, "YYYY-MM-DD HH:mm");
    const signUpEndsDateTime = moment(`${aftercareData.signUpEndsDate} ${aftercareData.signUpEndsTime}`, "YYYY-MM-DD HH:mm");
  
    if (signUpBeginsDateTime.isBefore(now)) {
      aftercareData.signUpBeginsDate = now.format("YYYY-MM-DD");
      aftercareData.signUpBeginsTime = now.format("HH:mm");
    }
  
    if (signUpEndsDateTime.isBefore(now)) {
      aftercareData.signUpEndsDate = now.format("YYYY-MM-DD");
      aftercareData.signUpEndsTime = now.format("HH:mm");
    }
  
    return aftercareData;
  }


// WEBPACK FOOTER //
// ./src/store/modules/aftercare-form/aftercare-form.module.js